import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { fetchPaginatedThirdPartyCompaniesStarted, getAvailableThirdPartyCompaniesPageNumber, getIsFetchingThirdPartyCompanies, getAvailableThirdPartyCompaniesFilters, getTotalThirdPartyCompanies, getAvailableThirdPartyCompaniesColumnSort, getAvailableThirdPartyCompaniesPageSize, getPaginatedThirdPartyCompanies, setAvailableThirdPartyCompaniesTableFilters, clearAvailableThirdPartyCompaniesTableFilters, setAvailableThirdPartyCompaniesTableColumnSort, setAvailableThirdPartyCompaniesPageSize, availableThirdPartyCompaniesPaginationNext, availableThirdPartyCompaniesPaginationPrevious, getThirdPartyDropdownOptions, setSelectedThirdPartyCompany } from './store';
import { OfficeBuilding } from '../../shared/icons';
import { Action } from '../../shared/modal/ActionModal';
import { ArkTableColumn, ColumnSort, TableFilterType } from '../../shared/table/types';
import { ArkTable } from '../../shared/table/ArkTable';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { HEIGHT_OF_THE_WIZARD } from '../my-companies/MyCompaniesInScopeForDora';
import { CompanyEntity } from '../../admin/entity/store';

export const ThirdPartyAvailableCompaniesTable: React.FC = () => {

    useFetchStarted([fetchPaginatedThirdPartyCompaniesStarted(1, 0)]);
    const [, screenHeight] = useWindowResize();

    const dispatch = useAppDispatch();
    const setSelectedThirdParty = useCallback((row: CompanyEntity) => dispatch(setSelectedThirdPartyCompany(row)), [dispatch]);

    const thirdPartyCompanies = useAppSelector(getPaginatedThirdPartyCompanies);
    const isLoading = useAppSelector(getIsFetchingThirdPartyCompanies);
    const pageNumber = useAppSelector(getAvailableThirdPartyCompaniesPageNumber);
    const totalThirdPartyCompanies = useAppSelector(getTotalThirdPartyCompanies);
    const filters = useAppSelector(getAvailableThirdPartyCompaniesFilters);
    const columnSort = useAppSelector(getAvailableThirdPartyCompaniesColumnSort);
    const pageSize = useAppSelector(getAvailableThirdPartyCompaniesPageSize);
    const thirdPartyDropdownOptions = useAppSelector(getThirdPartyDropdownOptions);

    const nextPage = useCallback(() => dispatch(availableThirdPartyCompaniesPaginationNext()), [dispatch]);
    const previousPage = useCallback(() => dispatch(availableThirdPartyCompaniesPaginationPrevious()), [dispatch]);

    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => { dispatch(setAvailableThirdPartyCompaniesTableFilters(key, value, type)); }, [dispatch]);
    const clearAllFilters = useCallback(() => dispatch(clearAvailableThirdPartyCompaniesTableFilters()), [dispatch]);
    const toggleColumnSort = useCallback((columnSort: ColumnSort) => dispatch(setAvailableThirdPartyCompaniesTableColumnSort(columnSort)), [dispatch]);
    const updatePageSize = useCallback((pageSize: number) => dispatch(setAvailableThirdPartyCompaniesPageSize(pageSize)), [dispatch]);

    const actions = useCallback((data: CompanyEntity): Action[] => [{ label: 'Open', icon: OfficeBuilding, onClick: () => setSelectedThirdParty(data) }], [setSelectedThirdParty]);

    const columnDefs: ArkTableColumn[] = [
        { id: 'name', header: 'Company Name', field: 'name', width: 0.95, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'actions', header: '', field: '', component: 'action', width: 0.05, actions: actions }
    ];

    const tableHeight = useMemo(() => (screenHeight * 0.75) - HEIGHT_OF_THE_WIZARD, [screenHeight]);

    const filterDropdownOptions = useMemo(() => ({
        companyName: thirdPartyDropdownOptions
    }), [thirdPartyDropdownOptions]);

    return (
        <ArkTable
            colDefs={columnDefs}
            rowData={thirdPartyCompanies}
            testId='third-party-company'
            isLoading={isLoading}
            page={pageNumber}
            total={totalThirdPartyCompanies}
            next={nextPage}
            previous={previousPage}
            filters={filters}
            updateFilter={updateFilter}
            clearAllFilters={clearAllFilters}
            onRowDoubleClicked={row => setSelectedThirdParty(row as CompanyEntity)}
            columnSort={columnSort}
            toggleSort={toggleColumnSort}
            pageSize={pageSize}
            updatePageSize={updatePageSize}
            filterDropdownOptions={filterDropdownOptions}
            height={tableHeight}
        />
    );
};
